import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEnvelope, FaLock, FaIdCard } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ClipLoader } from 'react-spinners';
import api from '../axiosConfig';
import './LoginRegister.css';

const LoginRegister = ({ isOpen, onClose }) => {
    const [isLogin, setIsLogin] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [passwordsMatch, setPasswordsMatch] = useState(true);
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [isEmailValid] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [isForgotPassword, setIsForgotPassword] = useState(false);
    const navigate = useNavigate();

    // Validate Form Inputs
    useEffect(() => {
        if (isForgotPassword) {
            setIsButtonDisabled(!email); // Only email needed for forgot password
        } else if (isLogin) {
            setIsButtonDisabled(!(email && password)); // Only email and password needed for login
        } else {
            const allFieldsFilled = email && password && confirmPassword && accountNumber;
            const passwordsMatch = password === confirmPassword;
            setPasswordsMatch(passwordsMatch);
            setIsButtonDisabled(!(allFieldsFilled && passwordsMatch));
        }
    }, [email, password, confirmPassword, accountNumber, isLogin, isForgotPassword]);

    // Handle Form Submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            if (isForgotPassword) {
                // Attempt to send a password reset request
                const response = await api.post('/password-reset-request', { email });
                toast.success(response.data.message);
                setEmail('');
                setIsForgotPassword(false);
            } else if (isLogin) {
                // Login: Only email and password are required
                const response = await api.post('/login', { email, password });
                localStorage.setItem('token', response.data.token);
                toast.success('Login successful!');
                navigate('/dashboard');
                onClose();
            } else {
                // Prevent registration if passwords don't match
                if (password !== confirmPassword) {
                    toast.error('Passwords do not match. Please try again.');
                    return;
                }

                // Registration: Requires email, account number, and password
                const response = await api.post('/register', {
                    email,
                    accountNumber,
                    password,
                });

                toast.success('Registration successful! Please log in to continue.');
                setIsLogin(true); // Switch to login mode after successful registration
                setEmail('');
                setPassword('');
                setConfirmPassword('');
                setAccountNumber('');
            }
        } catch (error) {
            console.error(error);
            const errorMessage = error.response?.data?.message || 'Please try again.';
            toast.error(isForgotPassword ? `Password reset failed: ${errorMessage}` : `Error: ${errorMessage}`);
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="popup-overlay">
            <div className="popup">
                {/* Close Button */}
                <button
                    className="close-btn"
                    onClick={() => {
                        setIsForgotPassword(false); // Reset Forgot Password state
                        onClose(); // Close the popup
                    }}
                    aria-label="Close"
                >
                    &times;
                </button>

                {/* Form Title */}
                <h2>{isForgotPassword ? 'Reset Password' : isLogin ? 'Login' : 'Register'}</h2>

                {/* Form */}
                <form onSubmit={handleSubmit}>
                    <div className={`input-container ${!isEmailValid ? 'input-error' : ''}`}>
                        <input
                            type="email"
                            placeholder="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                        <FaEnvelope className="icon" />
                    </div>
                    {!isEmailValid && <p className="error-text">Please enter a valid email address.</p>}

                    {!isForgotPassword && (
                        <div className="input-container">
                            <input
                                type="password"
                                placeholder="Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                            <FaLock className="icon" />
                        </div>
                    )}

                    {/* Registration-Specific Fields */}
                    {!isLogin && (
                        <>
                            <div className={`input-container ${!passwordsMatch ? 'input-error' : ''}`}>
                                <input
                                    type="password"
                                    placeholder="Confirm Password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                />
                                <FaLock className="icon" />
                            </div>

                            <div className="input-container">
                                <input
                                    type="text"
                                    placeholder="Account Number"
                                    value={accountNumber}
                                    onChange={(e) => setAccountNumber(e.target.value)}
                                    required
                                />
                                <FaIdCard className="icon" />
                            </div>

                            {/* Tooltip */}
                            <p className="tooltip-text">
                                What is my account number?
                                <span className="tooltip-hover">
                                    Your account number is assigned once your return has been filed.
                                </span>
                            </p>
                        </>
                    )}

                    {/* Submit Button */}
                    <button type="submit" className="btn-primary" disabled={isButtonDisabled || isLoading}>
                        {isLoading ? (
                            <ClipLoader size={20} color="#fff" />
                        ) : isForgotPassword ? (
                            'Send Link'
                        ) : isLogin ? (
                            'Login'
                        ) : (
                            'Register'
                        )}
                    </button>
                </form>

                {/* Toggle Text */}
                <p className="toggle-text">
                    {isForgotPassword ? (
                        <span className="toggle-link" onClick={() => setIsForgotPassword(false)}>
                            Back to Login
                        </span>
                    ) : isLogin ? (
                        <>
                            Don't have an account?{' '}
                            <span className="toggle-link" onClick={() => setIsLogin(false)}>
                                Register
                            </span>
                            <br />
                            <span
                                className="toggle-link forgot-password-link"
                                onClick={() => setIsForgotPassword(true)}
                            >
                                Forgot Your Password?
                            </span>
                        </>
                    ) : (
                        <>
                            Already have an account?{' '}
                            <span className="toggle-link" onClick={() => setIsLogin(true)}>
                                Login
                            </span>
                        </>
                    )}
                </p>
            </div>

            {/* Toast Notification Container */}
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </div>
    );
};

export default LoginRegister;
